import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  ChevronRight,
  Store as StoreIcon,
  Hash,
  FileText,
  Globe,
  Pencil,
  Trash2,
  Link2 as LinkIcon,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import {
  Page,
  Main,
  Card,
  Button,
  Modal,
  Field,
  LoadingState,
  ErrorState,
  EmptyState,
  inputClass,
} from '@/components/ui';
import { useApi, useMutation } from '@/hooks/useApi';
import api from '@/lib/api';
import { formatCurrency, formatDate, cn } from '@/lib/utils';
import { findCloseMatch, normalizeName } from '@/lib/match';
import type { Designer } from '@/lib/types';

const splitList = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
const ensureHttp = (u: string) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);
import type { StoreWithRelations } from './components/storeTypes';
import { StoreAssociates } from './components/StoreAssociates';
import { StoreLocations } from './components/StoreLocations';
import { StoreDesigners } from './components/StoreDesigners';

interface StoreForm {
  name: string;
  locationText: string;
  customerNumber: string;
  websiteUrl: string;
  aliases: string;
  notes: string;
}

const EMPTY_FORM: StoreForm = { name: '', locationText: '', customerNumber: '', websiteUrl: '', aliases: '', notes: '' };

// Link to the items list filtered to this store + status, newest purchase first.
function itemsLink(storeId: string, storeName: string, status: 'owned' | 'wishlist' | 'archived'): string {
  const params = new URLSearchParams({
    store_id: storeId,
    status,
    sort: 'acquired',
    order: 'desc',
    store_name: storeName,
  });
  return `/wardrobe/items?${params.toString()}`;
}

export function StoresPage() {
  const { data, loading, error, refetch } = useApi<{ stores: StoreWithRelations[] }>('/stores');
  const { data: designerData } = useApi<{ designers: Designer[] }>('/designers');
  const { mutate, loading: saving } = useMutation();

  const stores = data?.stores ?? [];
  const allDesigners = designerData?.designers ?? [];
  const spend = stores.reduce(
    (acc, s) => ({
      inCloset: acc.inCloset + (s.spendInCloset ?? 0),
      pending: acc.pending + (s.spendPending ?? 0),
      wishlist: acc.wishlist + (s.spendWishlist ?? 0),
      removed: acc.removed + (s.spendRemoved ?? 0),
    }),
    { inCloset: 0, pending: 0, wishlist: 0, removed: 0 },
  );

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [editingStore, setEditingStore] = useState<StoreWithRelations | null>(null);
  const [form, setForm] = useState<StoreForm>(EMPTY_FORM);

  // Designer link control (only on the Add Store form).
  const [linkDesignerId, setLinkDesignerId] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const openAdd = () => {
    setEditingStore(null);
    setForm(EMPTY_FORM);
    setLinkDesignerId('');
    setSaveError(null);
    setShowStoreModal(true);
  };

  const openEdit = (store: StoreWithRelations) => {
    setEditingStore(store);
    setForm({
      name: store.name,
      locationText: store.locationText ?? '',
      customerNumber: store.customerNumber ?? '',
      websiteUrl: store.websiteUrl ?? '',
      aliases: (store.aliases ?? []).join(', '),
      notes: store.notes ?? '',
    });
    setLinkDesignerId('');
    setSaveError(null);
    setShowStoreModal(true);
  };

  // alsoCreateDesigner: create a new designer with the same name as the store.
  const handleSaveStore = async (alsoCreateDesigner = false) => {
    if (!form.name.trim()) return;
    setSaveError(null);
    const base = {
      name: form.name.trim(),
      locationText: form.locationText.trim() || undefined,
      customerNumber: form.customerNumber.trim() || undefined,
      websiteUrl: form.websiteUrl.trim() || undefined,
      aliases: splitList(form.aliases),
      notes: form.notes.trim() || undefined,
    };
    if (editingStore) {
      // PATCH (useMutation has no patch); persist edits to the DB.
      setEditSaving(true);
      try {
        await api.patch(`/stores/${editingStore.id}`, base);
        setShowStoreModal(false);
        refetch();
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : 'Failed to save changes');
      } finally {
        setEditSaving(false);
      }
      return;
    }
    const res = await mutate('post', '/stores', {
      ...base,
      linkDesignerId: !alsoCreateDesigner && linkDesignerId ? linkDesignerId : undefined,
      createDesignerName: alsoCreateDesigner ? form.name.trim() : undefined,
    });
    if (res !== null) {
      setShowStoreModal(false);
      refetch();
    }
  };

  const handleDeleteStore = async (id: string) => {
    const res = await mutate('delete', `/stores/${id}`);
    if (res !== null) {
      setExpandedId(null);
      refetch();
    }
  };

  // When adding, surface an existing store the typed name likely duplicates.
  const closeStore = !editingStore && form.name.trim() ? findCloseMatch(form.name.trim(), stores) : null;

  const addAliasToExisting = async (store: StoreWithRelations) => {
    const next = [...(store.aliases ?? []), form.name.trim()];
    await api.patch(`/stores/${store.id}`, { aliases: next });
    setShowStoreModal(false);
    refetch();
  };

  // While editing, surface separate store records whose name matches an alias
  // being saved — these are duplicates that can be merged in.
  const aliasNorms = splitList(form.aliases).map(normalizeName).filter(Boolean);
  const dupStores = editingStore
    ? stores.filter((s) => s.id !== editingStore.id && aliasNorms.includes(normalizeName(s.name)))
    : [];

  // Explicit "merge a duplicate into this store" workflow.
  const [mergeInto, setMergeInto] = useState<StoreWithRelations | null>(null);
  const [mergeFromId, setMergeFromId] = useState('');
  const [merging, setMerging] = useState(false);

  const handleMergeStores = async () => {
    if (!mergeInto || !mergeFromId) return;
    setMerging(true);
    try {
      await api.post(`/stores/${mergeInto.id}/merge`, { fromStoreId: mergeFromId });
      setMergeInto(null);
      setMergeFromId('');
      setExpandedId(null);
      refetch();
    } finally {
      setMerging(false);
    }
  };

  const mergeStoreInto = async (fromStoreId: string) => {
    if (!editingStore) return;
    // Persist current edits (incl. aliases) first, then merge the duplicate in.
    await api.patch(`/stores/${editingStore.id}`, {
      name: form.name.trim(),
      locationText: form.locationText.trim() || null,
      customerNumber: form.customerNumber.trim() || null,
      websiteUrl: form.websiteUrl.trim() || null,
      aliases: splitList(form.aliases),
      notes: form.notes.trim() || null,
    });
    await api.post(`/stores/${editingStore.id}/merge`, { fromStoreId });
    setShowStoreModal(false);
    refetch();
  };

  return (
    <Page>
      <PageHeader section="My Stores" backHref="/profile" backLabel="Profile" />

      <Main className="space-y-5">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}

        {!loading && !error && (
          <>
            <Card className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Spending</p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(spend.inCloset)}
                    {spend.pending > 0 && (
                      <span className="text-base font-semibold text-muted-foreground">
                        {' '}
                        ({formatCurrency(spend.pending)} pending)
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    Wishlist: {formatCurrency(spend.wishlist)}
                  </p>
                  <p className="text-xs text-muted-foreground/70">
                    Removed: {formatCurrency(spend.removed)}
                  </p>
                </div>
                <p className="text-xs text-muted-foreground flex-shrink-0">{stores.length} stores</p>
              </div>
            </Card>

            <Button fullWidth onClick={openAdd}>
              <Plus className="w-4 h-4" />
              Add Store
            </Button>

            {stores.length === 0 ? (
              <EmptyState
                icon={<StoreIcon className="w-8 h-8" />}
                title="No stores yet"
                description="Add stores to track spending and sales associates."
              />
            ) : (
              <div className="space-y-3">
                {stores.map((store) => {
                  const expanded = expandedId === store.id;
                  const associates = store.associates ?? [];
                  const locations = store.locations ?? [];
                  const designers = store.designers ?? [];
                  return (
                    <Card key={store.id} className="overflow-hidden">
                      <button
                        onClick={() => setExpandedId(expanded ? null : store.id)}
                        className="w-full p-4 flex items-center gap-4 text-left"
                      >
                        <div className="p-3 bg-secondary rounded-xl flex-shrink-0">
                          <StoreIcon className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <h3 className="text-sm font-semibold text-foreground truncate">{store.name}</h3>
                            {store.websiteUrl && (
                              <WebsiteLink url={store.websiteUrl} />
                            )}
                          </div>
                          {store.locationText && (
                            <p className="text-xs text-muted-foreground truncate">{store.locationText}</p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-semibold text-foreground">
                            {formatCurrency(store.spendInCloset ?? 0)}
                            {(store.spendPending ?? 0) > 0 && (
                              <span className="text-[11px] font-medium text-muted-foreground">
                                {' '}
                                (+{formatCurrency(store.spendPending ?? 0)})
                              </span>
                            )}
                          </p>
                          <p className="text-[10px] text-muted-foreground/70">
                            Wishlist: {formatCurrency(store.spendWishlist ?? 0)}
                          </p>
                          <p className="text-[10px] text-muted-foreground/70">
                            Removed: {formatCurrency(store.spendRemoved ?? 0)}
                          </p>
                        </div>
                        <ChevronRight
                          className={cn('w-5 h-5 text-muted-foreground transition-transform flex-shrink-0', expanded && 'rotate-90')}
                        />
                      </button>

                      {expanded && (
                        <div className="border-t border-border">
                          <div className="p-4 space-y-3">
                            {store.customerNumber && (
                              <DetailRow icon={Hash} label="Customer Number" value={store.customerNumber} />
                            )}
                            {store.websiteUrl && (
                              <div className="flex items-start gap-3">
                                <Globe className="w-4 h-4 text-muted-foreground mt-0.5" />
                                <div>
                                  <p className="text-xs text-muted-foreground">Website</p>
                                  <a
                                    href={store.websiteUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-primary break-all"
                                  >
                                    {store.websiteUrl}
                                  </a>
                                </div>
                              </div>
                            )}
                            {store.notes && <DetailRow icon={FileText} label="Notes" value={store.notes} />}

                            <div className="flex gap-2 pt-2">
                              <button
                                onClick={() => openEdit(store)}
                                className="flex-1 flex items-center justify-center gap-2 py-2 bg-secondary rounded-lg text-xs font-medium text-foreground hover:bg-secondary/70 transition-colors"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                                Edit Store
                              </button>
                              <button
                                onClick={() => handleDeleteStore(store.id)}
                                className="flex items-center justify-center px-4 py-2 bg-destructive/10 rounded-lg text-xs font-medium text-destructive hover:bg-destructive/20 transition-colors"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>

                            {stores.length > 1 && (
                              <button
                                onClick={() => { setMergeInto(store); setMergeFromId(''); }}
                                className="w-full flex items-center justify-center gap-2 py-2 bg-secondary rounded-lg text-xs font-medium text-foreground hover:bg-secondary/70 transition-colors"
                              >
                                <LinkIcon className="w-3.5 h-3.5" />
                                Merge a duplicate into this store
                              </button>
                            )}
                          </div>

                          <StoreDesigners storeId={store.id} designers={designers} onChanged={refetch} />

                          <StoreLocations
                            storeId={store.id}
                            locations={locations}
                            associates={associates}
                            onChanged={refetch}
                          />

                          <StoreAssociates
                            storeId={store.id}
                            associates={associates}
                            locations={locations}
                            onChanged={refetch}
                          />

                          {/* Spending — each figure links to the filtered items list. */}
                          <div className="px-4 py-4 border-t border-border">
                            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                              Spending
                            </h4>
                            <div className="divide-y divide-border">
                              <SpendLink
                                label="In Closet"
                                amount={store.spendInCloset ?? 0}
                                count={store.countInCloset}
                                to={itemsLink(store.id, store.name, 'owned')}
                              />
                              <SpendLink
                                label="Wishlist"
                                amount={store.spendWishlist ?? 0}
                                count={store.countWishlist}
                                to={itemsLink(store.id, store.name, 'wishlist')}
                              />
                              <SpendLink
                                label="Removed"
                                amount={store.spendRemoved ?? 0}
                                count={store.countRemoved}
                                to={itemsLink(store.id, store.name, 'archived')}
                              />
                            </div>
                            {store.lastPurchase && (
                              <p className="text-[10px] text-muted-foreground mt-2">
                                Last purchased {formatDate(store.lastPurchase)}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
      </Main>

      {/* Add/Edit Store Modal */}
      <Modal
        isOpen={showStoreModal}
        onClose={() => setShowStoreModal(false)}
        title={editingStore ? 'Edit Store' : 'Add Store'}
        footer={
          editingStore ? (
            <Button fullWidth onClick={() => handleSaveStore()} disabled={!form.name.trim() || editSaving}>
              {editSaving ? 'Saving…' : 'Save Changes'}
            </Button>
          ) : (
            <div className="space-y-2">
              <Button fullWidth onClick={() => handleSaveStore(false)} disabled={!form.name.trim() || saving}>
                {saving ? 'Saving…' : 'Add Store'}
              </Button>
              <Button
                fullWidth
                variant="secondary"
                onClick={() => handleSaveStore(true)}
                disabled={!form.name.trim() || saving}
              >
                Add Store and Designer of the Same Name
              </Button>
            </div>
          )
        }
      >
        <div className="space-y-4">
          {closeStore && (
            <div className="rounded-lg bg-accent/10 border border-accent/30 px-3 py-2.5 text-xs">
              <p className="text-foreground">
                You already have <span className="font-semibold">{closeStore.name}</span> — same store?
              </p>
              <button
                type="button"
                onClick={() => addAliasToExisting(closeStore)}
                className="mt-1.5 inline-flex items-center gap-1 text-primary font-medium hover:underline"
              >
                <LinkIcon className="w-3 h-3" /> Add "{form.name.trim()}" as an alias of {closeStore.name}
              </button>
            </div>
          )}
          <Field label="Store Name *">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Neiman Marcus"
              className={inputClass}
            />
          </Field>
          <Field label="Location">
            <input
              type="text"
              value={form.locationText}
              onChange={(e) => setForm({ ...form, locationText: e.target.value })}
              placeholder="e.g., New York, NY or Online"
              className={inputClass}
            />
          </Field>
          <Field label="Customer Number">
            <input
              type="text"
              value={form.customerNumber}
              onChange={(e) => setForm({ ...form, customerNumber: e.target.value })}
              placeholder="e.g., NM-123456"
              className={inputClass}
            />
          </Field>
          <Field label="Website">
            <input
              type="url"
              value={form.websiteUrl}
              onChange={(e) => setForm({ ...form, websiteUrl: e.target.value })}
              placeholder="https://"
              className={inputClass}
            />
          </Field>
          <Field label="Aliases" hint="Other names this store goes by — e.g. mrporter.com. Comma-separated.">
            <input
              type="text"
              value={form.aliases}
              onChange={(e) => setForm({ ...form, aliases: e.target.value })}
              placeholder="mrporter.com, MR PORTER"
              className={inputClass}
            />
          </Field>
          {dupStores.map((dup) => (
            <div key={dup.id} className="rounded-lg bg-accent/10 border border-accent/30 px-3 py-2.5 text-xs">
              <p className="text-foreground">
                A separate store <span className="font-semibold">{dup.name}</span> matches this alias.
              </p>
              <button
                type="button"
                onClick={() => mergeStoreInto(dup.id)}
                className="mt-1.5 inline-flex items-center gap-1 text-primary font-medium hover:underline"
              >
                <LinkIcon className="w-3 h-3" /> Merge {dup.name} into {form.name.trim() || 'this store'} (moves its items here)
              </button>
            </div>
          ))}
          <Field label="Notes">
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Any notes about this store…"
              rows={3}
              className={cn(inputClass, 'resize-none')}
            />
          </Field>

          {!editingStore && allDesigners.length > 0 && (
            <div className="space-y-4 pt-2 border-t border-border">
              <Field
                label="Link an existing designer (optional)"
                hint="To create a brand-new designer named after this store, use the second button below."
              >
                <select
                  value={linkDesignerId}
                  onChange={(e) => setLinkDesignerId(e.target.value)}
                  className={inputClass}
                >
                  <option value="">— None —</option>
                  {allDesigners.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          )}

          {saveError && (
            <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{saveError}</p>
          )}
        </div>
      </Modal>

      {/* Merge duplicate store */}
      <Modal
        isOpen={mergeInto !== null}
        onClose={() => setMergeInto(null)}
        title={`Merge into ${mergeInto?.name ?? ''}`}
        footer={
          <Button fullWidth onClick={handleMergeStores} disabled={!mergeFromId || merging}>
            {merging ? 'Merging…' : 'Merge & Remove Duplicate'}
          </Button>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Pick the duplicate store. Its items, sales associates, and locations move to{' '}
            <span className="font-semibold text-foreground">{mergeInto?.name}</span>, its name becomes an alias, and the
            duplicate is removed.
          </p>
          <Field label="Duplicate to merge in">
            <select value={mergeFromId} onChange={(e) => setMergeFromId(e.target.value)} className={inputClass}>
              <option value="">— Select a store —</option>
              {stores
                .filter((s) => s.id !== mergeInto?.id)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
            </select>
          </Field>
        </div>
      </Modal>
    </Page>
  );
}

// Small link affordance shown inline after a name. Rendered as a click-stopping
// span (not an <a>) so it can live inside the row's toggle button.
function WebsiteLink({ url }: { url: string }) {
  const href = ensureHttp(url);
  const open = () => window.open(href, '_blank', 'noopener,noreferrer');
  return (
    <span
      role="link"
      tabIndex={0}
      title="Open website"
      onClick={(e) => { e.stopPropagation(); open(); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); open(); } }}
      className="flex-shrink-0 text-muted-foreground hover:text-primary transition-colors cursor-pointer"
    >
      <LinkIcon className="w-3.5 h-3.5" />
    </span>
  );
}

function SpendLink({ label, amount, count, to }: { label: string; amount: number; count?: number; to: string }) {
  return (
    <Link to={to} className="flex items-center justify-between py-2 group">
      <span className="text-sm text-foreground">
        {label}
        {count != null && (
          <span className="text-xs text-muted-foreground"> · {count} {count === 1 ? 'item' : 'items'}</span>
        )}
      </span>
      <span className="text-sm font-semibold text-primary group-hover:underline">
        {formatCurrency(amount)}
      </span>
    </Link>
  );
}

function DetailRow({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Hash;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-muted-foreground mt-0.5" />
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm text-foreground">{value}</p>
      </div>
    </div>
  );
}
